import styled from 'styled-components/macro';
import { BaseReactComponent } from '../../../types';

import {
  Alert,
  AlertTitle,
  AlertProps,
  AlertColor,
  lighten,
  darken
} from '@mui/material';
import HiButton from '../../atoms/HiButton';

export interface HiAlertProps extends BaseReactComponent {
  /**
   * The content of the component.
   */
  children?: AlertProps['children'];
  /**
   * The severity of the alert. This defines the color and icon used.
   * @default info
   */
  severity?: AlertProps['severity'];
  /**
   * The title of the component.
   */
  title?: AlertProps['title'];
  /**
   * The text displayed in the action button.
   */
  actionLabel?: string;
  /**
   * Callback fired when the action button is clicked.
   * @param {React.SyntheticEvent} event The event source of the callback.
   */
  onAction?: (event: React.SyntheticEvent) => void;
  /**
   * Callback fired when the component requests to be closed. When provided and no action prop is set, a close icon button is displayed that triggers the callback when clicked.
   * @param {React.SyntheticEvent} event The event source of the callback.
   */
  onClose?: AlertProps['onClose'];
  /**
   * Override Mui Alert props
   * (https://mui.com/material-ui/api/alert/).
   */
  AlertProps?: AlertProps;
}

/**
 * The Alert component displays a **short, important message** in a way that attracts the user's attention without interrupting the user's task.
 *
 * Alert components take place inside the content of a page. Usually, the alert takes its whole container’s width. It doesn’t dismiss automatically.
 *
 * It extends Mui's `Alert` component.
 */
const HiAlert = ({
  children,
  title,
  severity = 'info',
  actionLabel,
  onAction,
  onClose,
  AlertProps,
  ...other
}: HiAlertProps) => {
  const hasAction = onAction && actionLabel;

  return (
    <StyledAlert
      {...other}
      {...AlertProps}
      severity={severity}
      action={
        hasAction && (
          <HiButton
            variant="text"
            color="inherit"
            size="small"
            onClick={onAction}
          >
            {actionLabel}
          </HiButton>
        )
      }
      onClose={onClose}
      classes={{
        icon: 'HiAlert-icon',
        message: 'HiAlert-message',
        action: hasAction ? 'HiAlert-actionButton' : 'HiAlert-actionIcon'
      }}
    >
      {title && <AlertTitle className="HiAlert-title">{title}</AlertTitle>}
      {children}
    </StyledAlert>
  );
};

const StyledAlert = styled(Alert).attrs(
  ({ severity }: { severity: AlertColor }) => ({
    // If info severity, display in secondary
    xcolor: severity === 'info' ? 'secondary' : severity
  })
)`
  background-color: ${({ theme, xcolor }) =>
    lighten(theme.palette[xcolor].main, 0.9)};
  color: ${({ theme, xcolor }) => darken(theme.palette[xcolor].main, 0.6)};

  padding: 9px 16px;
  text-align: left;

  .HiAlert-icon {
    color: ${({ theme, xcolor }) => theme.palette[xcolor].main};
    font-size: 24px;
  }

  .HiAlert-title {
    color: ${({ theme, xcolor }) => darken(theme.palette[xcolor].main, 0.6)};
    line-height: 24px;
    margin-bottom: 0;
  }

  .HiAlert-message {
    padding: 10px 0 8px 0;
  }

  .HiAlert-actionButton {
    margin-right: -5px;
    padding-top: 10px;
    white-space: nowrap;
  }
`;

export default HiAlert;
